import DataLoader from 'app/ui_v2/components/data-loader/DataLoader';
import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router';

import { backOfficeAgenciesRoutes } from './backofficeAgenciesRoutes';
import { backOfficeInspectorsRoutes } from './backofficeInspectorsRoutes';
import { backOfficeTicketsRoutes } from './backofficeTicketsRoutes';

// Lazy load guards and layouts
const HasToBeBackofficeGuard = lazy(() => import('app/core/guards/HasToBeBackofficeGuard'));
const HasToBeLoggedInGuard = lazy(() => import('app/core/guards/HasToBeLoggedInGuard'));
const DefaultLayout = lazy(() => import('app/core/layout/DefaultLayout'));
const ProjectsListingPage = lazy(() => import('app/main/backoffice/bookings/ProjectsListingPage'));
const InspectionsListingSection = lazy(() => import('app/main/backoffice/inspections/InspectionsListingSection'));
const Error404Page = lazy(() => import('app/main/errors/Error404Page'));

// Lazy load page components

const InspectorProfilePage = lazy(
  () => import('../../../../main/construction/inspectors/profile/InspectorProfilePage'),
);
const BackofficeDashboard = lazy(() => import('../../../../main/backoffice/dashboard/BackofficeDashboard'));
const CreateInspector = lazy(() => import('../../../../main/construction/inspectors/create/CreateInspector'));
const InspectorsListingPage = lazy(() => import('../../../../main/construction/inspectors/InspectorsListingPage'));
const AccountProfilePage = lazy(() => import('../../../../main/agency/account/AccountProfilePage'));
const GeneralSettings = lazy(() => import('../../../../main/backoffice/settings/GeneralSettings'));

const BoCreateThreadPage = lazy(() => import('../../../../main/backoffice/messaging/BoCreateThreadPage'));
const BoMessagingPage = lazy(() => import('../../../../main/backoffice/messaging/BoMessagingPage'));
const BoNotificationLandingPage = lazy(() => import('../../../../main/backoffice/messaging/BoNotificationLandingPage'));

const PREFIX = '/backoffice';
const INSPECTORS_PREFIX = '/inspectors';

const backofficeRoutes: RouteObject[] = [
  {
    path: PREFIX + '/messaging',
    element: <Navigate to={PREFIX + '/messaging/inbox'} />,
  },
  {
    path: PREFIX,
    element: (
      <HasToBeLoggedInGuard>
        <HasToBeBackofficeGuard>
          <DefaultLayout />
        </HasToBeBackofficeGuard>
      </HasToBeLoggedInGuard>
    ),
    errorElement: <Error404Page />,
    children: [
      {
        path: 'dashboard',
        element: <Suspense fallback={<DataLoader />}>{<BackofficeDashboard />}</Suspense>,
      },
      {
        path: 'inspectors/new',
        element: <Suspense fallback={<DataLoader />}>{<CreateInspector />}</Suspense>,
      },
      {
        path: 'inspectors',
        element: <Suspense fallback={<DataLoader />}>{<InspectorsListingPage />}</Suspense>,
      },
      {
        path: 'account',
        element: <Suspense fallback={<DataLoader />}>{<AccountProfilePage />}</Suspense>,
      },
      {
        path: 'settings',
        element: <Suspense fallback={<DataLoader />}>{<GeneralSettings />}</Suspense>,
      },
      {
        path: 'tickets',
        children: backOfficeTicketsRoutes,
      },
      {
        path: 'inspections',
        element: <Suspense fallback={<DataLoader />}>{<InspectionsListingSection />}</Suspense>,
      },
      {
        path: 'projects',
        element: <Suspense fallback={<DataLoader />}>{<ProjectsListingPage />}</Suspense>,
      },
      {
        path: 'agency/inspector',
        children: backOfficeInspectorsRoutes,
      },
      {
        path: 'agency',
        children: backOfficeAgenciesRoutes,
      },
      // MESSAGES SECTION
      {
        path: 'messaging/create-thread/:recipientId',
        element: <BoCreateThreadPage />,
      },
      {
        path: 'messaging/create-thread/:recipientId/:jobId',
        element: <BoCreateThreadPage />,
      },

      {
        path: 'messaging/:sectionId',
        element: <BoMessagingPage />,
      },
      {
        path: 'messaging/:sectionId/:threadId',
        element: <BoMessagingPage />,
      },
      {
        path: 'notifications/:messageId',
        element: <BoNotificationLandingPage />,
      },
    ],
  },
  /**
   * DOB Inspectors Profile Routes
   */
  {
    path: INSPECTORS_PREFIX,
    element: (
      <HasToBeLoggedInGuard>
        <HasToBeBackofficeGuard>
          <DefaultLayout />
        </HasToBeBackofficeGuard>
      </HasToBeLoggedInGuard>
    ),
    errorElement: <Error404Page />,
    children: [
      {
        path: ':id/:sectionId',
        element: (
          <Suspense fallback={<DataLoader />}>
            <InspectorProfilePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<DataLoader />}>
            <InspectorProfilePage />
          </Suspense>
        ),
      },
    ],
  },
];

export default backofficeRoutes;
